import axios from "axios";
import React, { useContext, useState } from "react";
import { ConfigContext } from "../../Context/ConfigContext";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { formatDate } from "../GlobalFunctions";
import Flatpikr from "react-flatpickr";

const ProjectInspection = () => {
    const { project_id } = useParams();
    const { apiURL, apiHeaderFile, user_profile_id } = useContext(ConfigContext);

    const [formData, setFormData] = useState({
        inspection_date: formatDate(new Date()),
        attachments: [],
        remarks: "",
    });

    /* ---------- FILE SIZE VALIDATION (1MB) ---------- */
    const validateFileSize = (file) => {
        if (file.size > 1 * 1024 * 1024) {
            Swal.fire({
                title: "File size exceeds 1MB",
                text: `${file.name} is larger than 1MB`,
                icon: "error",
            });
            return false;
        }
        return true;
    };

    /* ---------- ATTACHMENTS ---------- */
    const handleAttachmentsChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const validFiles = files.filter(validateFileSize);

        setFormData((prev) => ({
            ...prev,
            attachments: [...prev.attachments, ...validFiles],
        }));

        e.target.value = "";
    };

    const removeAttachment = (index) => {
        setFormData((prev) => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index),
        }));
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    /* ---------- SUBMIT ---------- */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.inspection_date) {
            Swal.fire("Validation Error", "Inspection date is required", "warning");
            return;
        }

        const payload = new FormData();
        payload.append("project_id", project_id);
        payload.append("consultant_id", user_profile_id);
        payload.append("inspection_date", formData.inspection_date);
        payload.append("visit_remark", formData.remarks);

        formData.attachments.forEach((file, index) => {
            payload.append(`attachments[${index}]`, file);
        });

        try {
            const response = await axios.post(
                `${apiURL}api/LogProjectVisit`,
                payload,
                { headers: apiHeaderFile }
            );

            if (response.data.success) {
                Swal.fire("Success", "Inspection submitted successfully", "success");
                setFormData({
                    inspection_date: formatDate(new Date()),
                    attachments: [],
                    remarks: "",
                });
            }
        } catch (error) {
            Swal.fire("Error", "Something went wrong", "error");
        }
    };

    return (
        <div className="col-xxl-12">
            <div className="card mb-4">
                <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">Project Inspection</h5>
                    <Link to={`/ProjectInspectionList/${project_id}`} className="btn btn-light rounded-circle">
                        <i className="ri-list-check fs-16"></i>
                    </Link>
                </div>

                <div className="card-body">
                    <form className="row g-3" onSubmit={handleSubmit}>
                        {/* ================= TOP SECTION ================= */}

                        <div className="col-md-4 ms-auto">
                            <label className="form-label">Inspection Date</label>
                            <Flatpikr
                                className="form-control"
                                placeholder="Select Inspection Date"
                                options={{ dateFormat: "Y-m-d" }}
                                value={formData.inspection_date}
                                onChange={(date) =>
                                    setFormData({
                                        ...formData,
                                        inspection_date: date[0]
                                            ? formatDate(date[0])
                                            : "",
                                    })
                                }
                                required
                            />
                        </div>

                        <div className="col-md-12">
                            <label className="form-label">Remarks</label>
                            <textarea
                                className="form-control"
                                rows="4"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleInputChange}
                                placeholder="Enter remarks"
                                required
                            />
                        </div>

                        {/* ================= DIVIDER ================= */}
                        <div className="col-12">
                            <hr />
                        </div>

                        {/* ================= ATTACHMENTS SECTION ================= */}

                        <div className="col-md-12">
                            <label className="form-label">
                                Upload Attachments (Multiple, Max 1MB each)
                            </label>
                            <input
                                type="file"
                                className="form-control"
                                accept=".pdf,.doc,.docx,image/*"
                                multiple
                                onChange={handleAttachmentsChange}
                            />
                        </div>

                        {formData.attachments.length > 0 && (
                            <div className="col-md-12">
                                <label className="form-label">Attachments</label>
                                <ul className="list-group">
                                    {formData.attachments.map((file, index) => (
                                        <li
                                            key={index}
                                            className="list-group-item d-flex justify-content-between align-items-center"
                                        >
                                            <span className="text-truncate">{file.name}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-soft-danger"
                                                onClick={() => removeAttachment(index)}
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* ================= SUBMIT ================= */}
                        <div className="col-md-12 text-end">
                            <button type="submit" className="btn btn-primary">
                                Submit Inspection
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProjectInspection;
